import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/auth/apiClient';
import { queryKeys } from './queryKeys';
import type { SachkostenPatch, SachkostenRow, SachkostenWrite, UUID } from './types';

export function useSachkosten(planId: UUID | null) {
  return useQuery({
    queryKey: planId ? queryKeys.sach.plan(planId) : ['sach', 'none'],
    queryFn: async (): Promise<SachkostenRow[]> => {
      if (!planId) return [];
      const res = await apiClient.get<SachkostenRow[]>(`/plan/${planId}/sachkosten`);
      return res.data;
    },
    enabled: !!planId,
  });
}

export function useCreateSachkosten(planId: UUID) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: SachkostenWrite): Promise<SachkostenRow> => {
      const res = await apiClient.post<SachkostenRow>(`/plan/${planId}/sachkosten`, body);
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.sach.plan(planId) });
      void qc.invalidateQueries({ queryKey: queryKeys.snapshot.plan(planId) });
    },
  });
}

export function useUpdateSachkosten(planId: UUID) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: UUID; patch: SachkostenPatch }): Promise<SachkostenRow> => {
      const res = await apiClient.patch<SachkostenRow>(
        `/plan/${planId}/sachkosten/${vars.id}`,
        vars.patch,
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.sach.plan(planId) });
      void qc.invalidateQueries({ queryKey: queryKeys.snapshot.plan(planId) });
    },
  });
}

export function useDeleteSachkosten(planId: UUID) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: UUID): Promise<void> => {
      await apiClient.delete(`/plan/${planId}/sachkosten/${id}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.sach.plan(planId) });
      void qc.invalidateQueries({ queryKey: queryKeys.snapshot.plan(planId) });
    },
  });
}
